import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  GraduationCap, 
  Search, 
  Phone, 
  Mail, 
  IndianRupee, 
  UserCheck
} from 'lucide-react';
import { leadsService, type Lead } from '../../services/leadsService';

export function CrmAdmissions() {
  const [students, setStudents] = useState<Lead[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Lead[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');

  useEffect(() => {
    loadAdmissions();
  }, []);

  useEffect(() => {
    filterStudents();
  }, [students, searchQuery, courseFilter]);

  const loadAdmissions = async () => {
    try {
      const allLeads = await leadsService.listLeads();
      const enrolled = allLeads.filter(l => l.status === 'enrolled' || l.status === 'registered');
      setStudents(enrolled);
    } catch (err) {
      console.error('Error loading admissions:', err);
    }
  };

  const filterStudents = () => {
    let filtered = [...students];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        s => s.name.toLowerCase().includes(q) ||
             s.email?.toLowerCase().includes(q) ||
             s.phone.includes(q) ||
             s.courseInterest?.toLowerCase().includes(q)
      );
    }

    if (courseFilter !== 'all') {
      filtered = filtered.filter(s => s.courseInterest === courseFilter);
    }

    setFilteredStudents(filtered);
  };

  const totalEnrolled = students.filter(s => s.status === 'enrolled').length;
  const totalRegistered = students.filter(s => s.status === 'registered').length;
  const totalCollections = students.reduce((sum, s) => sum + (s.paid1 || 0) + (s.paid2 || 0), 0);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-slate-900/90 border border-slate-800/80 p-5 rounded-2xl shadow-lg">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-extrabold text-white tracking-tight font-heading">
              Student Admissions & Cohort Directory
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              {students.length} Enrolled Students
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Confirmed student admissions converted from counseling, fee status, and cohort assignments.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/fees"
            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-600/30 transition-all"
          >
            <IndianRupee className="w-4 h-4" />
            Manage Fee Payments
          </Link>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold text-slate-400 uppercase">Confirmed Enrolled</div>
            <div className="text-2xl font-black text-white font-heading mt-1">{totalEnrolled}</div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
            <GraduationCap className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold text-slate-400 uppercase">Tokens / Registered</div>
            <div className="text-2xl font-black text-white font-heading mt-1">{totalRegistered}</div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-pink-500/10 text-pink-400 flex items-center justify-center">
            <UserCheck className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold text-slate-400 uppercase">Total Revenue (INR)</div>
            <div className="text-2xl font-black text-emerald-400 font-heading mt-1">
              ₹{totalCollections.toLocaleString('en-IN')}
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center">
            <IndianRupee className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Filter toolbar */}
      <div className="flex flex-col sm:flex-row items-center gap-3 bg-slate-900/60 border border-slate-800/80 p-3 rounded-xl">
        <div className="relative flex-1 w-full">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search enrolled students by name, email, phone..."
            className="w-full pl-10 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-indigo-500"
          />
        </div>

        <select
          value={courseFilter}
          onChange={(e) => setCourseFilter(e.target.value)}
          className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
        >
          <option value="all">All Programs</option>
          <option value="Forward Deployed Engineer (FDE)">Forward Deployed Engineer (FDE)</option>
          <option value="DevOps & AWS Cloud Masterclass">DevOps & AWS Cloud Masterclass</option>
          <option value="Cloud FinOps Practitioner">Cloud FinOps Practitioner</option>
        </select>
      </div>

      {/* Admissions Table */}
      <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950 text-slate-400 uppercase tracking-wider text-[10px] border-b border-slate-800">
              <tr>
                <th className="p-4">Student</th>
                <th className="p-4">Contact</th>
                <th className="p-4">Enrolled Program</th>
                <th className="p-4">Fee Structure</th>
                <th className="p-4">Paid Amount</th>
                <th className="p-4">Due Balance</th>
                <th className="p-4">Admission Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 font-medium">
              {filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-500 text-xs">
                    No enrolled students found.
                  </td>
                </tr>
              ) : (
                filteredStudents.map((s) => {
                  const totalPaid = (s.paid1 || 0) + (s.paid2 || 0);
                  const isFullyPaid = s.due === 0 && (s.fee || 0) > 0;

                  return (
                    <tr key={s.leadId} className="hover:bg-slate-800/40 transition-colors">
                      <td className="p-4">
                        <div className="font-bold text-white text-sm">{s.name}</div>
                        <div className="text-[11px] text-slate-400">
                          Admitted: {new Date(s.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="p-4 space-y-0.5">
                        <div className="font-mono text-emerald-400 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {s.phone}
                        </div>
                        {s.email && (
                          <div className="text-slate-400 flex items-center gap-1 truncate max-w-[160px]">
                            <Mail className="w-3 h-3" />
                            {s.email}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="font-semibold text-slate-200">{s.courseInterest || 'FDE Program'}</span>
                      </td>
                      <td className="p-4 font-mono font-bold text-slate-200">
                        ₹{(s.fee || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-4 font-mono font-bold text-emerald-400">
                        ₹{totalPaid.toLocaleString('en-IN')}
                      </td>
                      <td className="p-4 font-mono font-bold text-amber-400">
                        ₹{(s.due || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-4">
                        {isFullyPaid ? (
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                            Fully Paid
                          </span>
                        ) : (
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-500/20 text-amber-300 border border-amber-500/30">
                            Partial / Due
                          </span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <Link
                          to={`/fees?student=${encodeURIComponent(s.name)}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-600/80 hover:bg-indigo-600 text-white rounded-lg text-xs font-semibold transition-colors"
                        >
                          <IndianRupee className="w-3 h-3" />
                          Receipt
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
